import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import { useSpaceVariableStore } from '@/modules/space/stores/SpaceVariableStore'
import { LoadPageFromSpacePageTemplateRequest } from '@/modules/spacepagetemplates/models/LoadPageFromSpacePageTemplateRequest'
import { SavePageAsSpacePageTemplateRequest } from '@/modules/spacepagetemplates/models/SavePageAsSpacePageTemplateRequest'
import SpacePageTemplate, {
  SpacePageTemplateCategoryName,
  TemplateUsageContext
} from '@/modules/spacepagetemplates/models/SpacePageTemplate'
import { UpdateSpacePageTemplateNameRequest } from '@/modules/spacepagetemplates/models/UpdateSpacePageTemplateNameRequest'
import ApiClient from '@/util/ApiClient'
import { defineStore } from 'pinia'
import { useSnackbarStore } from '@/modules/global/stores/SnackbarStore'
import { useOnboardingStore } from '@/modules/global/stores/OnboardingStore'
import { useViewSettingsStore } from '@/modules/user/stores/ViewSettingsStore'
import { enqueueApiErrorSnackbar } from '@/util/api-error-snackbar'
import i18n from '@/util/config/i18n'
import UpdateSpacePageTemplateRequest from '../models/UpdateSpacePageTemplateRequest'
import Space from '../models/Space'

export const ONBOARDING_TOUR_SKIPPED_SNACKBAR_DEDUPE_KEY = 'onboarding-tour-skipped'

/**
 * First-space onboarding is three steps, and each one is led by the action it is asking for: pick a
 * template, confirm it from its preview, then create the document in a list. The preview used to be
 * skipped during onboarding to save a click, which left the tour with nothing to point at between
 * the gallery and the destination dialog - guidance that names the button under it beats guidance
 * that floats beside the screen. Shared by all three steps so the two halves of "Step n of m"
 * cannot drift.
 */
export const ONBOARDING_TOUR_STEPS = 3

export type SpacePageTemplateState = {
  activeTemplateId: string | null
  templates: SpacePageTemplate[]
  frequentlyUsedTemplates: SpacePageTemplate[]
  isLoading: boolean
  isTemplateManagerModalOpen: boolean
  currentUsageContext: TemplateUsageContext | null
  replayOnboardingGuidance: boolean
  /**
   * Which step of the first-space onboarding tour is active: 1 = template gallery, 2 = a chosen
   * template's preview, 3 = the destination list. Steps 1 and 2 are two states of the same modal,
   * which is why this is a number here rather than a flag per host component.
   */
  onboardingTourStep: number
  /**
   * True while the first-space onboarding picker is open from welcome (not a Help/empty-state replay).
   * Used so closing the picker can land on Created by me even after the tour tips were dismissed.
   */
  firstSpaceOnboardingPickerActive: boolean
}
export const useSpacePageTemplateStore = defineStore('spacePageTemplate', {
  state: (): SpacePageTemplateState => ({
    activeTemplateId: null,
    templates: [],
    frequentlyUsedTemplates: [],
    isLoading: false,
    isTemplateManagerModalOpen: false,
    currentUsageContext: null,
    replayOnboardingGuidance: false,
    onboardingTourStep: 1,
    firstSpaceOnboardingPickerActive: false
  }),
  getters: {
    activeTemplate(): SpacePageTemplate | null {
      return this.templates.find(t => t.id === this.activeTemplateId) || null
    },
    localTemplates(): SpacePageTemplate[] {
      return this.templates.filter(t => !t.isGlobal)
    },
    spacePageTemplatesById() {
      return (templateId: string): SpacePageTemplate | null => {
        return this.templates.find(t => t.id === templateId) || null
      }
    },
    spacePageTemplatesByCategory() {
      return (category: SpacePageTemplateCategoryName): SpacePageTemplate[] => {
        return this.templates.filter(t => t.categories?.includes(category))
      }
    }
  },
  actions: {
    async loadTemplates(): Promise<void> {
      if (this.isLoading) {
        return
      }
      this.isLoading = true
      try {
        const response = await Promise.all([
          ApiClient.get<SpacePageTemplate[]>(`/space-page-templates`),
          ApiClient.get<SpacePageTemplate[]>(`/space-page-templates/global`)
        ])
        this.templates = response.flatMap(r => r.data)
      } catch (error) {
        // Both callers `await` this with no handler of their own, so the failure is reported
        // here rather than rethrown. Swallowing it made a failed load look like an empty
        // template list that never recovered.
        enqueueApiErrorSnackbar(error, i18n.t('templates.loadFailed') as string, {
          dedupeKey: 'space-page-templates-load-failed',
          onRetry: () => {
            this.loadTemplates()
          }
        })
      } finally {
        this.isLoading = false
      }
    },
    async loadFrequentlyUsedTemplates(): Promise<void> {
      const response = (await ApiClient.get<SpacePageTemplate[]>(`/space-page-templates/frequent`)).data
      this.frequentlyUsedTemplates = response
    },
    async updateTemplate(request: UpdateSpacePageTemplateRequest): Promise<void> {
      try {
        const template = (await ApiClient.put<SpacePageTemplate>(`/space-page-templates/${request.id}`, request)).data
        this.setTemplate(template)
      } catch (error) {
        console.error(error)
      }
    },
    async createTemplate(): Promise<void> {
      try {
        const newTempalte = (await ApiClient.post<SpacePageTemplate>(`/space-page-templates`, {})).data
        this.templates = [...this.templates, newTempalte]
        this.activeTemplateId = newTempalte.id
      } catch (error) {
        console.error(error)
      }
    },
    async deleteTemplate(id: string): Promise<boolean> {
      try {
        await ApiClient.delete(`/space-page-templates/${id}`)
        this.templates = this.templates.filter(t => t.id !== id)
        if (this.activeTemplateId === id) {
          this.activeTemplateId = null
        }
        this.loadFrequentlyUsedTemplates()
        return true
      } catch (error) {
        console.error(error)
        return false
      }
    },
    async updateTemplateName(data: UpdateSpacePageTemplateNameRequest): Promise<void> {
      try {
        const template = (
          await ApiClient.put<SpacePageTemplate>(`/space-page-templates/${data.id}/name`, { name: data.name })
        ).data
        if (template) {
          const index = this.templates.findIndex(t => t.id === template.id)
          if (index !== -1) {
            this.templates[index] = template
            this.templates = [...this.templates]
          }
        }
      } catch (error) {
        console.error(error)
      }
    },
    async duplicateTemplate(id: string): Promise<void> {
      try {
        const template = (await ApiClient.post<SpacePageTemplate>(`/space-page-templates/${id}/duplicate`)).data
        this.templates = [...this.templates, template]
        this.activeTemplateId = template.id
      } catch (error) {
        console.error(error)
      }
    },
    // Rejections propagate on purpose so callers can tell success from failure. This used to
    // swallow them, which made replacePageWithTemplate() report success after the template
    // never loaded.
    async loadPageFromTemplate(payload: LoadPageFromSpacePageTemplateRequest): Promise<void> {
      const response = await ApiClient({
        method: 'post',
        url: `/spaces/${payload.spaceId}/pages/${payload.pageId}/load-from-template`,
        params: { templateId: payload.templateId }
      })
      const space = response.data as Space
      useSpaceStore().setSpace(space)
      await useSpaceVariableStore().fetchVariables(payload.spaceId)

      this.loadFrequentlyUsedTemplates()
      this.activeTemplateId = null
    },
    async savePageAsTemplate({ pageId, spaceId }: SavePageAsSpacePageTemplateRequest): Promise<void> {
      try {
        const response = await ApiClient({
          method: 'post',
          url: `/spaces/${spaceId}/pages/${pageId}/save-as-template`
        })

        const template = response.data as SpacePageTemplate
        this.templates = [...this.templates, template]
      } catch (error) {
        console.error(error)
      }
    },
    async copyGlobalToTeamTemplate(id: string): Promise<void> {
      try {
        const template = (
          await ApiClient.post<SpacePageTemplate>(`/space-page-templates/${id}/copy-global-to-team-template`)
        ).data
        this.templates = [...this.templates, template]
        this.activeTemplateId = template.id
      } catch (error) {
        console.error(error)
      }
    },
    setActiveTemplate(id: string | null) {
      this.activeTemplateId = id
    },
    setTemplate(template: SpacePageTemplate) {
      const index = this.templates.findIndex(t => t.id === template.id)

      if (index !== -1) {
        this.templates.splice(index, 1, template)
      } else {
        this.templates.push(template)
      }
    },
    setTemplateManagerModal(isOpen: boolean, usage?: TemplateUsageContext, replayGuidance = false) {
      this.isTemplateManagerModalOpen = isOpen
      this.currentUsageContext = isOpen && usage ? usage : null
      this.replayOnboardingGuidance = isOpen && replayGuidance
      if (isOpen) {
        this.onboardingTourStep = 1
        this.firstSpaceOnboardingPickerActive = useOnboardingStore().isPending && !replayGuidance
      } else {
        this.firstSpaceOnboardingPickerActive = false
      }
    },
    /**
     * Mirrors where the flow actually is, so it has to be able to go backwards: leaving the
     * destination dialog returns to the gallery. Callers set it unconditionally - whether a step is
     * *shown* is each host's own gate, and keeping that out of here means the number never depends
     * on whether the tour happens to be dismissed.
     */
    setOnboardingTourStep(step: number) {
      this.onboardingTourStep = step
    },
    clearFirstSpaceOnboardingPickerActive() {
      this.firstSpaceOnboardingPickerActive = false
    },
    async dismissOnboardingGuidance(): Promise<void> {
      // Leaving the tips leaves the flow: otherwise closing the picker steps back to the welcome
      // modal, whose only action is to reopen the picker. Done first and locally - a failed
      // view-settings write must not be able to trap a founder in that loop.
      useOnboardingStore().completeIfPending()
      try {
        await useViewSettingsStore().updateViewSettings({ firstSpaceOnboardingDismissed: true })
      } catch (error) {
        enqueueApiErrorSnackbar(error, i18n.t('profileSettings.error') as string)
        return
      }
      useSnackbarStore().enqueue({
        label: i18n.t('framework.onboardingTourSkippedHint') as string,
        variant: 'default',
        dedupeKey: ONBOARDING_TOUR_SKIPPED_SNACKBAR_DEDUPE_KEY
      })
    },
    async replayFirstSpaceTour(): Promise<void> {
      const viewSettingsStore = useViewSettingsStore()
      await viewSettingsStore.updateViewSettings({ firstSpaceOnboardingDismissed: false })
      this.setTemplateManagerModal(true, TemplateUsageContext.CREATE_SPACE_SELECT_SPACE_LIST, true)
    }
  }
})

export type SpacePageTemplateStore = ReturnType<typeof useSpacePageTemplateStore>
